import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import React from 'react';

import { RefresherEventDetail } from '@ionic/core';
import { close } from 'ionicons/icons';

import Comprobante from '../../components/Reintegro/Comprobante';
import Documentacion from '../../components/Reintegro/Documentacion';
import DocumentacionExtra from '../../components/Reintegro/DocumentacionExtra';
import VeterinarioForm from '../../components/Reintegro/VeterinarioForm';
import WizardContent from '../../components/Reintegro/WizardContent';
import Alert from '../../components/UI/Alert';
import Navigation from '../../components/UI/Navigation';
import { useDocumentacion } from '../../context/DocumentacionContext';
import { usePets } from '../../context/PetsContext';
import { useSVFs } from '../../context/SVFsContext';
import { useSiniestros } from '../../context/SiniestrosContext';
import { useAlert } from '../../hooks/UI/useAlert';
import { useModal } from '../../hooks/UI/useModal';
import { useToast } from '../../hooks/UI/useToast';
import { VeterinarioForm as VeterinarioFormType } from '../../types/Reintegro/Veterinario/types';
import { ComprobanteGalleryItem as GalleryItem } from '../../types/Reintegro/types';
import { SVF } from '../../types/Siniestro/types';
import { isDocumentacionCompleta } from '../../utils/documentacion';
import { useGetOtherFiles } from '../Reintegro/Documentacion/useDocumentacion';
import { useVetFormSend } from '../Reintegro/useVetFormSend';

export const sendFields = [
  'nombre',
  'apellido',
  'fechaAtencion',
] as (keyof VeterinarioFormType)[];

export const reSendFields = ['email', 'telefono'] as (keyof VeterinarioFormType)[];

export const useSiniestroPage = () => {
  const { currentPet } = usePets();
  const { currentSiniestro } = useSiniestros();
  const { denuncia_administrativa, fecha_carga } = currentSiniestro || {};
  const { documentacion } = useDocumentacion();
  const {
    SVFs,
    mutateSVFs,
    comprobantesBySVFs,
    vetFormId_Ref,
    isValidating: isComprobantesValidating,
  } = useSVFs();

  const isVacuna = currentSiniestro?.tipo === 'vacuna';

  const {
    otherFiles: vacunaComprobantes,
    isLoading: isVacunaComprobantesLoading,
    mutate: mutateVacunaComprobantes,
  } = useGetOtherFiles(
    isVacuna ? String(currentSiniestro?.id) : undefined,
    isVacuna ? 'comprobante_pago' : undefined,
  );

  // Verificar si hay al menos un SVF con estado COMPLETADO
  const hasCompletedSVF = useMemo(
    () => SVFs?.some((svf) => svf.vetForm?.estado === 'COMPLETADO') ?? false,
    [SVFs],
  );

  // Documentación extra (informe y estudio) - solo si hay SVFs en estado COMPLETADO
  const { otherFiles: informeFiles, isLoading: isInformeLoading } = useGetOtherFiles(
    hasCompletedSVF && currentSiniestro?.id ? String(currentSiniestro.id) : undefined,
    hasCompletedSVF ? 'informe' : undefined,
  );

  const { otherFiles: estudioFiles, isLoading: isEstudioLoading } = useGetOtherFiles(
    hasCompletedSVF && currentSiniestro?.id ? String(currentSiniestro.id) : undefined,
    hasCompletedSVF ? 'estudio' : undefined,
  );

  const isDocumentacionExtraLoading = isInformeLoading || isEstudioLoading;
  const documentacionExtraCount =
    (informeFiles?.length ?? 0) + (estudioFiles?.length ?? 0);

  const { isOpen, content, modalProps, openModal, closeModal } = useModal();
  const { alertState, showAlert, hideAlert } = useAlert();
  const { toastState, showToast, hideToast } = useToast();

  const [galleryItems, setGalleryItems] = useState<GalleryItem[] | null>(null);
  const [galleryTitle, setGalleryTitle] = useState<string>('Comprobantes');
  const [resendingVetFormId, setResendingVetFormId] = useState<number | null>(null);

  const submittedVetValues = useRef<VeterinarioFormType | null>(null);
  const isRefresherRunning = useRef<boolean>(false);
  const evaluatedSiniestroId = useRef<string | number | null>(null);

  const documentacionCompleta = useMemo(
    () => isDocumentacionCompleta(documentacion),
    [documentacion],
  );
  const hasReintegros = isVacuna
    ? vacunaComprobantes.length > 0
    : SVFs && SVFs.length > 0;
  // isInitialLoading: mostrar spinner fullscreen hasta tener los datos necesarios
  // - Vacunas: esperar documentación y primera carga de comprobantes
  // - No vacunas: esperar SVFs (sin depender de isValidating para evitar falsos negativos)
  // Nota: isVacunaComprobantesLoading es true solo en primera carga, no en revalidaciones
  const isVacunaInitialLoading =
    isVacuna && (!documentacion || isVacunaComprobantesLoading);
  const isNoVacunaInitialLoading = !isVacuna && SVFs === undefined;
  const isInitialLoading =
    !isRefresherRunning.current && (isVacunaInitialLoading || isNoVacunaInitialLoading);

  const { resendFromSVF } = useVetFormSend({
    currentSiniestroId: String(currentSiniestro?.id),
    vetFormIdRef: vetFormId_Ref,
    submittedVetValues,
    onNext: () => {},
    showAlert,
    mutateSVFs,
  });

  const openWizard = () => {
    submittedVetValues.current = null;

    openModal(
      <WizardContent
        documentacionCompleta={documentacionCompleta}
        isVacuna={isVacuna}
        onClose={closeModal}
        submittedVetValues={submittedVetValues}
        mutateVacunaComprobantes={isVacuna ? mutateVacunaComprobantes : undefined}
      />,
      { navigation: false },
    );
  };

  const openVeterinario = useCallback(
    (svf?: SVF) => {
      const isEditMode = !!svf?.vet;

      // Setear el vetFormId en el ref del contexto para que esté disponible en edición
      if (svf?.vetFormId) {
        vetFormId_Ref.current = svf.vetFormId;
      }

      if (svf?.vet) {
        submittedVetValues.current = {
          nombre: svf.vet.nombre || '',
          apellido: svf.vet.apellido || '',
          email: svf.vet.mail || '',
          telefono: svf.vet.telefono || '',
          fechaAtencion: svf.vetForm?.fechaAtencionSegunCliente || '',
        };
      } else {
        submittedVetValues.current = null;
      }

      const formKey = `vet-form-${svf?.vetFormId ?? 'new'}-${Date.now()}`;

      openModal(
        <>
          <Navigation startIcon={close} onBack={closeModal} />
          <VeterinarioForm
            key={formKey}
            submittedVetValues={submittedVetValues}
            onNext={closeModal}
            showAlert={showAlert}
            buttonLabel="Guardar y regresar"
            isEditMode={isEditMode}
            vetFormId={svf?.vetFormId}
          />
          <Alert
            isOpen={alertState.isOpen}
            header={alertState.header}
            message={alertState.message}
            buttons={alertState.buttons}
            onDidDismiss={hideAlert}
          />
        </>,
        { navigation: false },
      );
    },
    [openModal, closeModal, showAlert, alertState, hideAlert, vetFormId_Ref],
  );

  const openDocumentacion = useCallback(() => {
    openModal(
      <>
        <Navigation startIcon={close} onBack={closeModal} />
        <Documentacion onNext={closeModal} buttonLabel="Guardar y regresar" />
      </>,
      { navigation: false },
    );
  }, [openModal, closeModal]);

  const openComprobante = useCallback(
    (vetFormId: number) => {
      vetFormId_Ref.current = vetFormId;
      openModal(
        <>
          <Navigation startIcon={close} onBack={closeModal} />
          <Comprobante onSuccess={closeModal} onClose={closeModal} />
        </>,
        { navigation: false },
      );
    },
    [openModal, closeModal, vetFormId_Ref],
  );

  const openComprobanteVacuna = useCallback(() => {
    openModal(
      <>
        <Navigation startIcon={close} onBack={closeModal} />
        <Comprobante
          isVacuna
          mutateVacunaComprobantes={mutateVacunaComprobantes}
          onSuccess={closeModal}
          onClose={closeModal}
        />
      </>,
      { navigation: false },
    );
  }, [openModal, closeModal, mutateVacunaComprobantes]);

  const openDocumentacionExtra = useCallback(() => {
    if (!currentSiniestro?.id) return;

    openModal(
      <>
        <Navigation startIcon={close} onBack={closeModal} />
        <DocumentacionExtra
          siniestroId={String(currentSiniestro.id)}
          onClose={closeModal}
        />
      </>,
      { navigation: false },
    );
  }, [openModal, closeModal, currentSiniestro]);

  const openViewDocumentacionExtra = useCallback(() => {
    const allFiles: Array<{ file: string; concepto: string }> = [];

    if (Array.isArray(informeFiles)) {
      informeFiles.forEach((url: string) => {
        allFiles.push({ file: url, concepto: 'Historia clínica' });
      });
    }

    if (Array.isArray(estudioFiles)) {
      estudioFiles.forEach((url: string) => {
        allFiles.push({ file: url, concepto: 'Estudios' });
      });
    }

    if (allFiles.length === 0) return;

    (document.activeElement as HTMLElement)?.blur();

    setGalleryTitle('Documentación extra');
    setGalleryItems(allFiles);
  }, [informeFiles, estudioFiles]);

  const openComprobantesGallery = useCallback(
    (svfId: number) => {
      const comprobantes = comprobantesBySVFs?.[svfId] ?? [];
      if (!comprobantes.length) return;

      (document.activeElement as HTMLElement)?.blur();

      setGalleryTitle('Comprobantes');
      setGalleryItems(
        comprobantes.map((c) => ({
          file: c.file,
          concepto: c.concepto,
          monto: Number(c.monto) > 0 ? c.monto : undefined,
        })),
      );
    },
    [comprobantesBySVFs],
  );

  const openVacunaComprobantesGallery = useCallback(() => {
    if (!vacunaComprobantes.length) return;

    (document.activeElement as HTMLElement)?.blur();

    setGalleryTitle('Comprobantes');
    // El endpoint devuelve un array de URLs (strings)
    setGalleryItems(
      vacunaComprobantes.map((url: string) => ({
        file: url,
      })),
    );
  }, [vacunaComprobantes]);

  const closeGallery = useCallback(() => {
    setGalleryItems(null);
    setGalleryTitle('Comprobantes');
  }, []);

  const onResendVeterinario = useCallback(
    (svf: SVF) => {
      if (!svf?.vet || !svf?.vetFormId) return;

      showAlert({
        header: 'Reenviar consulta',
        message:
          '¿Estás seguro que deseas reenviar esta consulta a tu veterinario? Asegurate que los datos sean correctos y esten actualizados.',
        buttons: [
          { text: 'Cancelar', role: 'cancel' },
          {
            text: 'Reenviar',
            handler: async () => {
              setResendingVetFormId(svf.vetFormId);

              const fechaAtencion = svf.vetForm?.fechaAtencionSegunCliente || '';

              const success = await resendFromSVF(
                {
                  nombre: svf.vet.nombre || '',
                  apellido: '',
                  email: svf.vet.mail,
                  telefono: svf.vet.telefono,
                  fechaAtencion,
                },
                {
                  siniestroId: String(currentSiniestro!.id),
                  veterinario: {
                    nombre: svf.vet.nombre,
                    mail: svf.vet.mail,
                    telefono: svf.vet.telefono,
                    tipo: svf.vet.tipo,
                  },
                  fechaAtencion,
                  derivaciones: [],
                },
                svf.vetFormId,
                true, // skipFetch: desde ReintegroCard solo POST, sin fetch adicional
              );

              setResendingVetFormId(null);

              if (success) {
                showAlert({
                  header: 'Link reenviado con éxito!',
                  message: 'Se envió un email a su veterinario.',
                  buttons: [{ text: 'Entendido' }],
                });
              }
            },
          },
        ],
      });
    },
    [resendFromSVF, currentSiniestro, showAlert],
  );

  const onRefresh = useCallback(
    async (event: CustomEvent<RefresherEventDetail>) => {
      try {
        isRefresherRunning.current = true;
        if (isVacuna) {
          await mutateVacunaComprobantes();
        } else {
          await mutateSVFs();
        }
      } finally {
        isRefresherRunning.current = false;
        event.detail.complete();
      }
    },
    [isVacuna, mutateSVFs, mutateVacunaComprobantes],
  );

  // Refrescar comprobantes de vacuna cuando la documentación se completa,
  // por si el backend los retiene hasta que la documentación esté lista
  const prevDocCompleta = useRef(documentacionCompleta);
  useEffect(() => {
    if (isVacuna && !prevDocCompleta.current && documentacionCompleta) {
      mutateVacunaComprobantes();
    }
    prevDocCompleta.current = documentacionCompleta;
  }, [isVacuna, documentacionCompleta, mutateVacunaComprobantes]);

  useEffect(() => {
    if (isInitialLoading || !currentSiniestro?.id || !documentacion) return;

    // Si ya evaluamos este siniestro, no hacer nada
    if (evaluatedSiniestroId.current === currentSiniestro.id) return;

    if (hasReintegros && documentacionCompleta === false) {
      const timer = setTimeout(() => {
        showToast({
          message:
            'Todavía falta cargar documentación. Completala para avanzar con el reintegro.',
          color: 'warning',
        });
      }, 2000);

      evaluatedSiniestroId.current = currentSiniestro.id;

      return () => clearTimeout(timer);
    }

    evaluatedSiniestroId.current = currentSiniestro.id;
  }, [
    isInitialLoading,
    currentSiniestro,
    documentacion,
    hasReintegros,
    documentacionCompleta,
    showToast,
  ]);

  return {
    /* data */
    currentPet,
    currentSiniestro,
    SVFs,
    comprobantesBySVFs,
    isComprobantesValidating,
    vacunaComprobantes,
    isVacunaComprobantesLoading,
    denuncia_administrativa,
    fecha_carga,
    documentacionCompleta,
    isVacuna,
    hasReintegros,
    hasCompletedSVF,
    documentacionExtraCount,
    isDocumentacionExtraLoading,

    /* vacuna */
    openVacunaComprobantesGallery,
    openComprobanteVacuna,

    /* actions */
    onResendVeterinario,
    resendingVetFormId,
    openWizard,
    openVeterinario,
    openDocumentacion,
    openComprobante,
    openDocumentacionExtra,
    openViewDocumentacionExtra,

    /* modal */
    isOpen,
    content,
    modalProps,
    closeModal,

    /* alert */
    alertState,
    hideAlert,

    /* gallery */
    galleryItems,
    galleryTitle,
    isGalleryOpen: !!galleryItems,
    openComprobantesGallery,
    closeGallery,

    /* refresh */
    onRefresh,
    isInitialLoading,

    /* toast */
    toastState,
    hideToast,
  };
};
